import './ui/styles.css';
import { DEFAULT_CHARACTER, type JoinedRoom } from '@kokun/shared';
import { api, ApiError } from './app/api';
import { ui } from './app/game-store';
import { connection } from './multiplayer/connection';
import { webglAvailable } from './game/core/engine';
import { World3D } from './game/world/world3d';
import { MenuScene } from './game/world/menu-scene';
import { GameSession } from './game/world/game-session';
import { audio } from './game/audio/audio';
import { openCreator } from './character-creator/creator';
import { askName, renderWelcome } from './ui/screens/welcome';
import { mountHud } from './ui/screens/hud';
import { h, toast } from './ui/dom';

const app = document.getElementById('app')!;
const overlay = document.getElementById('ui')!;

function fatal(msg: string) {
  overlay.replaceChildren(h('div', { class: 'screen fatal panel' }, h('h1', null, 'El Kokun Game'), h('p', null, msg)));
}

if (!webglAvailable()) {
  fatal('Tu navegador o tu equipo no admite WebGL, que es necesario para el 3D. Prueba con una versión reciente de Chrome, Firefox, Edge o Safari, o activa la aceleración por hardware.');
  throw new Error('WebGL no disponible');
}

const world = new World3D(app);
if (world.engine.info.maxTexture < 2048) toast('Tu GPU es limitada: se ha reducido la calidad gráfica.', 'warn');

let menu: MenuScene | null = new MenuScene(world);
let session: GameSession | null = null;
let unmountHud: (() => void) | null = null;
let welcomeEl: (HTMLElement & { cleanup?: () => void }) | null = null;

function showWelcome() {
  welcomeEl?.cleanup?.();
  welcomeEl = renderWelcome({
    quickplay: () => online(() => connection.request<JoinedRoom>('room:quickplay')),
    createRoom: (visibility) => online(() => connection.request<JoinedRoom>('room:create', { visibility })),
    joinRoom: (code) => online(() => connection.request<JoinedRoom>('room:join', { code })),
    practice: () => startGame(null),
    openCreator: () => editCharacter(),
    ensureProfile,
    logout: () => {
      connection.disconnect();
      ui.set({ profile: null });
      showWelcome();
    },
    engineInfo: () => world.engine.info,
  });
  overlay.replaceChildren(welcomeEl);
  ui.set({ screen: 'welcome' });
}

async function ensureProfile(): Promise<boolean> {
  if (ui.get().profile) return true;
  const ok = await askName();
  if (ok) showWelcome();
  return ok;
}

/** Garantiza la conexión de tiempo real antes de pedir una sala. */
function ensureConnected(): Promise<void> {
  const s = connection.socket;
  if (s?.connected) return Promise.resolve();
  return new Promise((resolve, reject) => {
    connection.connect(api.token ?? '');
    const sock = connection.socket!;
    const timer = setTimeout(() => reject(new Error('No se puede conectar con el servidor.')), 10_000);
    sock.once('connect', () => {
      clearTimeout(timer);
      resolve();
    });
    sock.once('connect_error', (err) => {
      clearTimeout(timer);
      if (err.message === 'unauthorized') {
        api.token = null;
        ui.set({ profile: null });
        connection.disconnect();
        reject(new Error('Tu sesión ha caducado. Vuelve a elegir nombre.'));
      } else reject(new Error(err.message === 'too_many_connections' ? 'Demasiadas conexiones desde tu red.' : 'No se puede conectar con el servidor.'));
    });
    bindGlobalSocket();
  });
}

let globalBound = false;
function bindGlobalSocket() {
  const s = connection.socket!;
  if ((s as unknown as { __kokun?: boolean }).__kokun) return;
  (s as unknown as { __kokun?: boolean }).__kokun = true;
  globalBound = true;
  s.on('room:left', (p) => {
    toast(p.reason === 'kicked' ? 'El anfitrión te ha expulsado de la sala.' : 'La sala se ha cerrado.', 'warn', 5000);
    exitGame();
  });
  s.on('session:replaced', () => {
    toast('Has abierto el juego en otra pestaña o dispositivo.', 'warn', 6000);
    exitGame();
  });
  // Reconexión tras recargar o tras un corte: el servidor nos devuelve a nuestra sala
  s.on('room:joined', (j) => {
    if (!session) startGame(j);
  });
}

async function online(join: () => Promise<JoinedRoom>) {
  await ensureConnected();
  const j = await join();
  startGame(j);
}

function startGame(j: JoinedRoom | null) {
  audio.unlock();
  welcomeEl?.cleanup?.();
  overlay.replaceChildren();
  menu?.dispose();
  menu = null;
  session?.dispose();
  unmountHud?.();
  const profile = ui.get().profile;
  const character = profile?.player.character ?? DEFAULT_CHARACTER;
  session = new GameSession(world, j, character, j?.you ?? 'local');
  unmountHud = mountHud({ leave: () => void leave(), openCreator: () => editCharacter(), session: () => session, input: world.input, engineInfo: () => world.engine.info });
  ui.set({ screen: 'game', paused: false, results: null });
  if (!j) {
    toast('Modo práctica: explora libremente. Haz clic para controlar la cámara.', 'info', 5000);
    ui.set({ room: null, mode: 'offline' });
  } else toast(`Sala ${j.room.code}: haz clic en la pantalla para controlar la cámara.`, 'info', 5000);
}

function exitGame() {
  session?.dispose();
  session = null;
  unmountHud?.();
  unmountHud = null;
  world.input.unlock();
  ui.set({ room: null, mission: null, minigame: null, results: null, mode: null, chat: [], prompt: null, cooldowns: {} });
  if (!menu) menu = new MenuScene(world);
  showWelcome();
}

async function leave() {
  if (ui.get().mode === 'online') await connection.request('room:leave').catch(() => undefined);
  exitGame();
}

function editCharacter() {
  world.input.unlock();
  world.input.enabled = false;
  ui.set({ screen: 'creator' });
  openCreator(() => {
    world.input.enabled = !!session;
    ui.set({ screen: session ? 'game' : 'welcome' });
    if (session && ui.get().mode === 'online') connection.emit('character:refresh');
    if (!session) showWelcome();
  });
}

// ------------------------------------------------------------------ arranque
async function boot() {
  if (api.token) {
    try {
      ui.set({ profile: await api.me() });
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) api.token = null;
    }
  }
  showWelcome();
  document.body.classList.add('ready');
  // Enlace de invitación: ?sala=CODIGO
  const code = new URLSearchParams(location.search).get('sala');
  if (code) {
    history.replaceState(null, '', location.pathname);
    if (await ensureProfile()) online(() => connection.request<JoinedRoom>('room:join', { code: code.toUpperCase() })).catch((e) => toast((e as Error).message, 'error'));
  } else if (ui.get().profile) {
    // Si había una partida en curso (recarga), el servidor nos reincorpora automáticamente
    ensureConnected().catch(() => undefined);
  }
  void globalBound;
}

window.addEventListener('pointerdown', () => audio.unlock(), { once: true });
window.addEventListener('keydown', () => audio.unlock(), { once: true });
void boot();

// Gancho de depuración para pruebas E2E (solo lectura)
(window as unknown as { __kokun: unknown }).__kokun = { ui, connection };
